import { useState } from 'react';
import { useMutation } from '@apollo/client';

import {
  CreateUploadDocument,
  CreateUploadMutation,
  UploadScope,
} from '@sandtech-hub-monorepo/ehub-client';
import { useErrorHandler } from './useErrorHandler';

export const useUploadFile = () => {
  const [uploadFileLoading, setUploadFileLoading] = useState(false);
  const [createUpload, { loading: createUploadLoading }] =
    useMutation(CreateUploadDocument);

  const { handleApolloError, handleGeneralAppError } = useErrorHandler();

  /**
   * Uploads a file to S3 through a presigned POST and returns its `s3://` URI,
   * or `undefined` if anything went wrong. Callers must treat `undefined` as a
   * failure and not save a record referencing it — the URI is a promise that
   * the object exists, and on a failed upload it does not.
   */
  const uploadFile = async (scope: UploadScope, file: File) => {
    const { data } = await createUpload({
      variables: {
        input: { scope: scope, filename: file.name },
      },
      onError: handleApolloError,
      onCompleted: (data: CreateUploadMutation) => {},
    });

    if (!data?.createUpload) {
      return undefined;
    }

    const formData = new FormData();
    data.createUpload.fields.forEach(
      (kvPair: { key: string; value: string | Blob }) => {
        formData.append(kvPair.key, kvPair.value);
      }
    );

    // S3 requires the file part last — everything before it is the policy.
    formData.append('file', file);

    setUploadFileLoading(true);

    const response = await fetch(data.createUpload.uploadUrl, {
      method: 'POST',
      body: formData,
    }).catch((error) => {
      // Usually the upload bucket's CORS allow-list missing this origin: S3
      // stores the object and returns 204, but the browser blocks the response
      // before we can read it. Logged because the exception is otherwise the
      // only evidence of which of the two it was.
      console.error(`Upload POST to S3 failed for ${file.name}:`, error);
      return null;
    });

    setUploadFileLoading(false);

    // `null` means fetch rejected: a network failure or a CORS rejection. A
    // non-ok response means S3 refused the POST — a policy violation such as
    // an oversized file answers 403 with an XML body, which fetch resolves
    // perfectly happily. Both used to be swallowed into a console.log, so the
    // caller received a fileUrl for an object that was never stored and went
    // on to save a record pointing at a key that does not exist.
    //
    // The message says nothing about the cause: whoever sees it is not
    // technical, and from here the two cases are genuinely indistinguishable.
    // It also makes no claim about what was or was not saved — this hook is
    // shared by profile photos, CVs, badge images and note attachments, and
    // the callers do not all recover the same way.
    if (!response?.ok) {
      handleGeneralAppError(
        `Couldn't upload "${file.name}"`,
        'Please try again. If it keeps happening, contact your support team.'
      );
      return undefined;
    }

    return data.createUpload.fileUrl;
  };

  return { uploadFile, loading: createUploadLoading || uploadFileLoading };
};
